import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { formatMoney } from '../../utils/money';

export default function StipendSetupPage() {
  const [stipends, setStipends] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ user_id: '', monthly_amount: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stipendData, userData] = await Promise.all([
        api.getStipends(),
        api.getUsers(),
      ]);
      setStipends(Array.isArray(stipendData) ? stipendData : []);
      setUsers(Array.isArray(userData) ? userData.filter(u => u.is_active) : []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(e) {
    e.preventDefault();
    if (!form.user_id || !form.monthly_amount) return;
    setSaving(true);
    setError('');
    try {
      await api.setStipend({
        user_id: Number(form.user_id),
        monthly_amount: Number(form.monthly_amount),
      });
      setSuccess('Stipend updated!');
      setForm({ user_id: '', monthly_amount: '' });
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  // Get current MTD spend for each stipend user
  const [mtdSpends, setMtdSpends] = useState({});
  useEffect(() => {
    async function loadSpends() {
      const spends = {};
      for (const s of stipends) {
        try {
          const info = await api.getStipendInfo(s.user_id);
          spends[s.user_id] = info;
        } catch { /* ignore */ }
      }
      setMtdSpends(spends);
    }
    if (stipends.length > 0) loadSpends();
  }, [stipends]);

  if (loading) return <div className="admin-page"><div className="loading-spinner">Loading...</div></div>;

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Stipend Setup</h1>
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        Set monthly expense reimbursement limits per employee. Employees will be warned when submissions exceed their remaining balance.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Set/Update form */}
      <form onSubmit={handleSave} style={{
        background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0',
        display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24,
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="form-label">Employee</label>
          <select className="form-input" value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))} required>
            <option value="">-- Select --</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 160 }}>
          <label className="form-label">Monthly Amount</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: 700 }}>$</span>
            <input className="form-input" type="number" step="0.01" min="0"
              style={{ paddingLeft: 24 }}
              value={form.monthly_amount} onChange={e => setForm(f => ({ ...f, monthly_amount: e.target.value }))}
              placeholder="0.00" required />
          </div>
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Set Stipend'}
        </button>
      </form>

      {/* Current stipends */}
      {stipends.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8', fontSize: 13 }}>
          No stipends configured yet. Set one above.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {stipends.map(s => {
            const info = mtdSpends[s.user_id];
            const pct = info && s.monthly_amount > 0 ? (info.mtd_spent / s.monthly_amount) * 100 : 0;
            return (
              <div key={s.user_id} style={{
                background: '#fff', borderRadius: 12, padding: '16px 20px',
                border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 16,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{s.full_name || s.username}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {formatMoney(s.monthly_amount)} / month
                  </div>
                </div>
                {info && (
                  <div style={{ flex: 1, maxWidth: 280 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                      <span style={{ color: '#64748b' }}>MTD: {formatMoney(info.mtd_spent)}</span>
                      <span style={{ color: info.remaining <= 0 ? '#dc2626' : '#10b981', fontWeight: 700 }}>
                        {formatMoney(Math.max(0, info.remaining))} left
                      </span>
                    </div>
                    <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 3, transition: 'width 0.3s',
                        width: `${Math.min(100, pct)}%`,
                        background: pct >= 100 ? '#dc2626' : pct >= 75 ? '#f59e0b' : '#6d28d9',
                      }} />
                    </div>
                  </div>
                )}
                <button onClick={() => setForm({ user_id: String(s.user_id), monthly_amount: String(s.monthly_amount) })}
                  style={{ fontSize: 12, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                  Edit
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
